import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Search as SearchIcon, PackageSearch } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Reveal } from "@/components/common/Reveal";
import ProductCard from "@/components/product/ProductCard";
import EmptyState from "@/components/common/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { productsService } from "@/services/productsService";
import { useCategories, useBrands, useSite } from "@/context/SiteDataContext";
import { brandName, categoryName, subcategoryName } from "@/lib/labels";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "az", label: "Product Name A–Z" },
  { value: "za", label: "Product Name Z–A" },
  { value: "brand", label: "Brand" },
];

function FilterPanel({ params, setParam, activeCat }) {
  const CATEGORIES = useCategories();
  const BRANDS = useBrands();
  const brandsWithProducts = BRANDS.filter((b) => b.categories.length > 0);
  const Group = ({ title, children }) => (
    <div className="py-5 border-b border-border last:border-0">
      <p className="eyebrow mb-3">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
  const Opt = ({ active, onClick, children, testId }) => (
    <button onClick={onClick} data-testid={testId} className={`block w-full text-left text-sm py-1 transition-colors ${active ? "text-aqua font-semibold" : "text-greyish hover:text-ink"}`}>
      {children}
    </button>
  );

  return (
    <div>
      <Group title="Category">
        <Opt active={!params.category} onClick={() => setParam({ category: null, subcategory: null })} testId="filter-cat-all">All Categories</Opt>
        {CATEGORIES.map((c) => (
          <Opt key={c.slug} active={params.category === c.slug} onClick={() => setParam({ category: c.slug, subcategory: null })} testId={`filter-cat-${c.slug}`}>{c.name}</Opt>
        ))}
      </Group>

      {activeCat && activeCat.subcategories.length > 0 && (
        <Group title="Subcategory">
          <Opt active={!params.subcategory} onClick={() => setParam({ subcategory: null })} testId="filter-sub-all">All</Opt>
          {activeCat.subcategories.map((s) => (
            <Opt key={s.slug} active={params.subcategory === s.slug} onClick={() => setParam({ subcategory: s.slug })} testId={`filter-sub-${s.slug}`}>{s.name}</Opt>
          ))}
        </Group>
      )}

      <Group title="Brand">
        <Opt active={!params.brand} onClick={() => setParam({ brand: null })} testId="filter-brand-all">All Brands</Opt>
        {brandsWithProducts.map((b) => (
          <Opt key={b.slug} active={params.brand === b.slug} onClick={() => setParam({ brand: b.slug })} testId={`filter-brand-${b.slug}`}>{b.name}</Opt>
        ))}
      </Group>
    </div>
  );
}

export default function Catalogue() {
  const CATEGORIES = useCategories();
  const SITE = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [all, setAll] = useState(null);
  const [sort, setSort] = useState("featured");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [mobileFilters, setMobileFilters] = useState(false);

  useEffect(() => {
    productsService.getProducts().then(setAll);
  }, []);

  useEffect(() => { setQ(searchParams.get("q") || ""); }, [searchParams]);

  const params = {
    category: searchParams.get("category"),
    subcategory: searchParams.get("subcategory"),
    brand: searchParams.get("brand"),
  };
  const activeCat = CATEGORIES.find((c) => c.slug === params.category);

  const setParam = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v == null) next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next);
  };

  const clearAll = () => { setSearchParams(new URLSearchParams()); setQ(""); };

  const hasFilters = params.category || params.subcategory || params.brand || q;

  const filtered = useMemo(() => {
    if (!all) return null;
    let list = all.filter((p) => {
      if (params.category && p.category !== params.category) return false;
      if (params.subcategory && p.subcategory !== params.subcategory) return false;
      if (params.brand && p.brand !== params.brand) return false;
      if (q.trim()) {
        const hay = [p.name, brandName(p.brand), categoryName(p.category), p.subcategory ? subcategoryName(p.subcategory) : "", p.description].join(" ").toLowerCase();
        if (!hay.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "az") return a.name.localeCompare(b.name);
      if (sort === "za") return b.name.localeCompare(a.name);
      if (sort === "brand") return brandName(a.brand).localeCompare(brandName(b.brand));
      return (b.featured === true) - (a.featured === true);
    });
    return list;
  }, [all, params.category, params.subcategory, params.brand, q, sort]);

  const crumbs = [{ label: "Products", to: "/products" }];
  if (activeCat) crumbs.push({ label: activeCat.name });

  return (
    <>
      <Seo routeKey="products" title="Products" description="Browse laboratory water purification systems, glassware washers and balances. Filter by category, subcategory and brand." canonical={`https://${SITE.website}/products`} />
      <div className="border-b border-border bg-[#f9fafb] grid-tech">
        <div className="container-x py-8 md:py-10">
          <Breadcrumbs crumbs={crumbs} />
          <Reveal>
            <h1 className="mt-5 text-3xl md:text-5xl font-bold tracking-tight text-ink">
              {params.brand ? brandName(params.brand) : activeCat ? activeCat.name : "Product Catalogue"}
            </h1>
            <p className="mt-3 text-greyish max-w-2xl">
              {params.subcategory ? subcategoryName(params.subcategory) : activeCat ? activeCat.shortDescription : "Explore our full range of laboratory technologies. Add products to your inquiry cart and send us an enquiry."}
            </p>
          </Reveal>
        </div>
      </div>

      <div className="container-x py-10 md:py-14">
        <div className="grid lg:grid-cols-12 gap-10">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold text-ink">Filters</p>
                {hasFilters && <button onClick={clearAll} data-testid="clear-filters" className="text-xs font-semibold text-aqua hover:underline">Clear all</button>}
              </div>
              <FilterPanel params={params} setParam={setParam} activeCat={activeCat} />
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-8">
              <div className="relative flex-1 max-w-sm">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-greyish" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="catalogue-search"
                  placeholder="Search products…"
                  className="w-full pl-11 pr-4 py-3 border border-border rounded-md text-base outline-none focus:border-aqua focus:ring-2 focus:ring-aqua/20 transition-all"
                />
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setMobileFilters(true)} data-testid="mobile-filters-btn" className="lg:hidden inline-flex items-center gap-2 border border-border px-4 py-2.5 rounded-md text-sm font-semibold">
                  <SlidersHorizontal className="w-4 h-4" /> Filters
                </button>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="sort-select" className="w-[190px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SORTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filtered && (
              <p className="text-sm text-greyish mb-6" data-testid="results-count">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</p>
            )}

            {!filtered ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white border border-border rounded-lg overflow-hidden">
                    <Skeleton className="aspect-square w-full" />
                    <div className="p-5 space-y-3"><Skeleton className="h-3 w-1/2" /><Skeleton className="h-5 w-3/4" /><Skeleton className="h-10 w-full" /></div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState icon={PackageSearch} title="No products found" description="Try adjusting or clearing your filters to see more results." actionLabel="Clear filters" onAction={clearAll} testId="no-products" />
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="left" className="w-[85%] max-w-sm overflow-y-auto" data-testid="mobile-filter-drawer">
          <SheetHeader className="mb-2 flex flex-row items-center justify-between">
            <SheetTitle>Filters</SheetTitle>
            {hasFilters && <button onClick={clearAll} className="text-xs font-semibold text-aqua">Clear all</button>}
          </SheetHeader>
          <FilterPanel params={params} setParam={(u) => { setParam(u); }} activeCat={activeCat} />
          <button onClick={() => setMobileFilters(false)} className="mt-6 w-full bg-aqua text-white py-3 rounded-md font-semibold">Show results</button>
        </SheetContent>
      </Sheet>
    </>
  );
}
